import { Tabs } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useState } from 'react';

import { EditorContext, ModalServiceContext, RicosContext } from './../../ricos-context';
import type { PmGifNode } from './../../_shared/pm-node-types';
import { Panel, PanelHeader } from './../../ui-components';

import { GiphySelector } from '../components/giphy-selector';
import { INSERT_GIF_MODAL, INSERT_GIF_MODAL_GIF_TAB } from '../data-hooks';
import type { GiphyPluginEditorConfig } from '../types';
import { GIPHY_TYPE } from '../types';

type Props = {
  activeTab?: 'gifs' | 'stickers';
  nodeId?: string;
  modalId: string;
  getGiphyUrl: GiphyPluginEditorConfig['getGiphyUrl'];
};

export const GiphyInsertModal: FC<Props> = ({
  activeTab = 'gifs',
  nodeId,
  modalId,
  getGiphyUrl,
}) => {
  const { t, isMobile, languageDir } = useContext(RicosContext);
  const { getPublicCommands } = useContext(EditorContext);
  const modalService = useContext(ModalServiceContext) || {};
  const [activeId, setActiveId] = useState<'gifs' | 'stickers'>(activeTab);
  const [modalContainerRef, setModalContainerRef] = React.useState<HTMLDivElement | null>(null);

  const closeModal = () => {
    modalService.closeModal(modalId);
  };

  const onGifAdd = (gif: Omit<PmGifNode['attrs'], 'id'>) => {
    const publicCommands = getPublicCommands();
    let insertGif = () => {
      publicCommands?.insertBlockWithBlankLines(GIPHY_TYPE, gif);
    };
    if (nodeId) {
      insertGif = () => {
        publicCommands?.setBlock(nodeId, GIPHY_TYPE, gif);
      };
    }
    setTimeout(insertGif, 50);
  };

  return (
    <Panel
      onCloseButtonClick={closeModal}
      width={isMobile ? '100%' : 430}
      height={isMobile ? '100vh' : 576}
    >
      <PanelHeader title={t('GiphyPlugin_InsertModal_Header_Title')} />

      <div dir={languageDir}>
        <div data-hook={INSERT_GIF_MODAL} ref={setModalContainerRef} />
        <Tabs
          dataHook={INSERT_GIF_MODAL_GIF_TAB}
          size="small"
          alignment="center"
          type="uniformFull"
          minWidth={0}
          activeId={activeId}
          onClick={value => setActiveId(value.id as 'gifs' | 'stickers')}
          items={[
            { id: 'gifs', title: t('GiphyPlugin_InsertModal_Gifs') },
            { id: 'stickers', title: t('GiphyPlugin_InsertModal_Stickers') },
          ]}
        />

        <GiphySelector
          key={activeId}
          onCloseRequested={closeModal}
          onGifAdd={onGifAdd}
          getGiphyUrl={getGiphyUrl}
          category={activeId}
          modalRef={modalContainerRef}
          modalId={modalId}
        />
      </div>
    </Panel>
  );
};
